import axios from 'axios';
import uuid from 'uuid';

import { generateChecksum } from 'helpers/generateChecksum';

import apiClient from 'apiClient';
import { request } from 'javascripts/services/v3ApiClient';

const FILE_EXTENSION_REGEX = /(?:\.([^.]+))?$/;

class UploadService {
  uploadFileToS3 = async (file, onProgress = undefined) => {
    const { url, data } = await this._signatureForFile(file);
    const location = await this._uploadToS3(url, data, onProgress);

    return location;
  };

  uploadDataURLToS3 = async (dataURL) => {
    const blob = await (await fetch(dataURL)).blob();
    const location = await this.uploadFileToS3(blob);

    return location;
  };

  _uploadToS3 = async (url, data, onProgress) => {
    const client = axios.create();
    const config = { responseType: 'document', timeout: 0 }; // No enforced timeout
    if (onProgress && typeof onProgress === 'function') {
      config.onUploadProgress = onProgress;
    }

    try {
      const response = await client.post(url, data, config);
      return this._locationFromAwsResponse(response);
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  };

  securelyUploadFileToS3 = async (file, onProgress = undefined) => {
    const checksum = await generateChecksum(file);
    const contentType = file.type;
    const filename = file.name;

    const {
      data: {
        identifier,
        directUpload: { headers, url },
      },
    } = await request({
      url: 'upload_requests',
      method: 'POST',
      data: {
        checksum,
        content_type: contentType,
        filename,
      },
    });

    await this._securelyUploadToS3(url, file, {
      headers,
      onProgress,
    });

    return identifier;
  };

  securelyUploadDataURLToS3 = async (dataURL) => {
    const blob = await (await fetch(dataURL)).blob();
    const identifier = await this.securelyUploadFileToS3(blob);

    return identifier;
  };

  _securelyUploadToS3 = async (url, data, config) => {
    const client = axios.create();
    const updatedConfig = config;

    if (config.onProgress && typeof config.onProgress === 'function') {
      updatedConfig.onUploadProgress = config.onProgress;
    }

    try {
      const response = await client.put(url, data, updatedConfig);
      return response;
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  };

  _getFileExtension = (file) => {
    const extensionFromFileType = file.type.split('/').pop(); // if the filename has no extension (which is not unlikely)
    const extension =
      FILE_EXTENSION_REGEX.exec(file.name)[1] || extensionFromFileType;

    return extension;
  };

  _createFilename = (file) => {
    const key = uuid.v4();
    const extension = this._getFileExtension(file);

    return `${key}.${extension}`;
  };

  _signatureForFile = async (file) => {
    const response = await apiClient.post('signed_url', {
      filename: this._createFilename(file),
      mime_type: file.type,
    });

    return {
      url: response.url,
      data: this._formDataForResponse(response, file),
    };
  };

  _formDataForResponse = (response, file) => {
    const data = new FormData();
    Object.keys(response.data).forEach((key) => {
      data.append(key, response.data[key]);
    });
    data.append('file', file);

    return data;
  };

  _locationFromAwsResponse = (response) => {
    // grab file Location from XML in response
    return response.data.querySelector('Location').textContent;
  };
}

export default UploadService;
